import { format } from "date-fns";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { BookingService } from "@/services/BookingService";
import useIsMobile from "@/hooks/useIsMobile";

interface UseCalendarBookingsQueryOptions {
  locationSlug: string;
  date: Date | null;
  calendarType: "day" | "week";
  enabled?: boolean;
}

/**
 * React Query hook to fetch calendar bookings with intelligent method selection
 *
 * Query Key: ["bookings", "calendar", locationSlug, dateKey, calendarType, isMobile]
 * Cache: 3 minutes (can be manually refreshed as needed)
 *
 * Preserves mobile payload optimization:
 * - Mobile: Always uses single day (getBookingsForLocationAndDate)
 * - Desktop: Uses appropriate method based on calendar type (day/week)
 *
 * Backend optimization via `swing` parameter:
 * - Day: Current date only
 * - Week: 7 days forward (swing=7)
 * - Month: 30 days forward (swing=30)
 *
 * @param locationSlug - The location slug to fetch bookings for
 * @param date - The date to fetch bookings for
 * @param calendarType - The calendar view type (day/week)
 * @param enabled - Whether the query should run (default: true)
 */
export function useCalendarBookingsQuery({
  locationSlug,
  date,
  calendarType,
  enabled = true,
}: UseCalendarBookingsQueryOptions) {
  const isMobile = useIsMobile();
  const bookingService = useMemo(() => new BookingService(), []);

  // Normalize date to avoid cache key fluctuations
  const dateKey = date ? format(date, "yyyy-MM-dd") : null;

  return useQuery({
    queryKey: ["bookings", "calendar", locationSlug, dateKey, calendarType, isMobile],
    queryFn: async () => {
      if (!date) return [];

      const showBaseOnly = true; // Don't expand recurring bookings

      // Mobile always uses day view with single date
      if (isMobile) {
        return bookingService.getBookingsForLocationAndDate(
          locationSlug,
          date,
          showBaseOnly
        );
      }

      // Desktop uses calendar type to determine method
      switch (calendarType) {
        case "week":
          return bookingService.getBookingsForWeek(
            date,
            locationSlug,
            showBaseOnly
          );
        case "day":
        default:
          return bookingService.getBookingsForLocationAndDate(
            locationSlug,
            date,
            showBaseOnly
          );
      }
    },
    enabled: enabled && !!locationSlug && !!date,
    staleTime: 3 * 60 * 1000, // 3 minutes - can be manually refreshed
    refetchInterval: 60 * 1000, // Poll every 60 seconds to pick up changes from other users
    refetchOnWindowFocus: true,
    retry: 2,
  });
}
